import {FrmSceneLayout} from "../../drawfrm/FrmSceneLayout";
import {FrameGridView} from "../frameGrid/FrameGridScene";
import {TopDownView} from "../frameProfile/TopDownScene";
import {ExportScene} from "./ExportScene";
import {GlassScheduleRow} from "./GlassSchedule";
import {FrameBlockItem} from "../frameGrid/FrameBlockItem";
import {liteGlass} from "../../model/glass/MdGlass";
import {itemGlazing} from "../../model/frame/MdFrameBlock";
import {frameOuterRect} from "../frameGrid/FrameOuterDimension";
import {RenderAnchorSection} from "../../../../../../api/ElevationTool";

// simplified frame (screen) elevation for a submittal: reuses the frame-grid view,
// which already draws the overall outer dimensions and the per-cell inner
// dimensions, plus a plan (top-down) section beneath it, on the plain export look.
export class ScreenExportScene extends ExportScene {
    // the built grid, for the glass schedule (its laid-out cells are the cut-outs).
    protected grid: FrameGridView | null = null;

    protected buildContent(): FrmSceneLayout {
        const grid = this.track(new FrameGridView(this.elev, input => this.add(input)));
        this.grid = grid;

        if(!this.includeTopDown()) return grid.getLayout();

        // the plan section under the grid. Some handings (pocket/slide) have no swing,
        // so the top-down can't build — fall back to the grid alone in that case.
        let topDown: TopDownView | null = null;
        try {
            topDown = this.track(new TopDownView(this.elev, input => this.add(input), {multiview: true, hideLabels: true}));
        } catch(e) {
            console.warn("screen export: top-down section skipped", e);
        }

        if(!topDown) return grid.getLayout();

        // center the plan section in ~half the width (flexible spacers on each side)
        // so it reads narrower than the full-width grid above it.
        return FrmSceneLayout.column(
            grid.getLayout().grow(1),
            FrmSceneLayout.row(
                FrmSceneLayout.spacing(0, 0).grow(1),
                topDown.getLayout().grow(4),
                FrmSceneLayout.spacing(0, 0).grow(1),
            ).grow(0.2),
        ).gap(15);
    }

    // whether to draw the plan (top-down) section beneath the grid. Defaults on for the
    // screen export; the hardware export drops it.
    protected includeTopDown(): boolean {
        return true;
    }

    // every lite cell in grid order. A cell's laid-out rect is its cut-out: the cell
    // edges pin to the profile rabbets with no gap.
    protected glassScheduleRows(): GlassScheduleRow[] {
        if(!this.grid) return [];

        const rows: GlassScheduleRow[] = [];
        for(const cell of this.grid.grid.getItems() as FrameBlockItem[]) {
            if(cell.model.kind !== "lite" || !cell.layoutRect) continue;
            rows.push({
                mark: cell.model.mark || "",
                glazing: itemGlazing(cell.model),
                cutOut: cell.cutOut(),
                glass: liteGlass(cell.model),
            });
        }
        return rows;
    }

    protected showAnchors(): boolean {
        return true;
    }

    protected anchorSections(): RenderAnchorSection[] {
        return this.grid?.anchorMarks.measure() ?? [];
    }

    outsideSize(): {width: number; height: number} | null {
        if(!this.grid) return null;

        const outer = frameOuterRect(this.grid.grid, this.elev.frameSpec);
        if(!outer) return null;

        return {width: outer.width, height: outer.height};
    }

    // draw all dimensions (overall, section sizes, stick thicknesses) stacked
    // outside the frame, engineering-drawing style.
    protected externalDimensions(): boolean {
        return true;
    }

    // hide hardware product-code labels (frame crosshairs + the door handle/lock).
    protected hideHardwareLabels(): boolean {
        return true;
    }
}
